import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { GridCell, GridData } from '../../models/types';
import type { Rack } from '../../models/types';
import { useWarehouseGrid } from '@modules/WarehouseMap/hooks/useWarehouseGrid';
import { useWarehouseRacks } from '@modules/WarehouseMap/hooks/useWarehouseRacks';
import { useWarehouse } from '@modules/WarehouseMap/hooks/useWarehouse';
import { useUpdateRack } from '@modules/WarehouseMap/hooks/useUpdateRack';
import { useWarehouseHeatmap, useRackHeatmap } from '@modules/WarehouseMap/hooks/useWarehouseHeatmap';
import { WarehouseMapApiAdapter } from '../../adapters/WarehouseMapApiAdapter';
import { createHttpClient } from '@adapters/HttpClient';
import { apiClientWH } from '@shared/services/ApiClient';
import { useToast } from '@shared/hooks/use-toast';
import { GridCanvas } from '@modules/WarehouseMap/components/display/GridCanvas';
import { WarehouseBreadcrumb } from './Breadcrumb';
import { RackLocationModal } from './RackLocationModal';
import { RackFormModal } from './RackFormModal';
import { WarehouseAdminToolbar } from './WarehouseAdminToolbar';
import { useParams } from 'react-router-dom';
import { useWarehouseMapStore } from '../../stores/warehouseMapStore';

// Default rack configuration
const RACK_WIDTH = 2;
const RACK_HEIGHT = 4;
const RACK_DEPTH = 1;

export const WarehouseMapView = () => {
  const { t } = useTranslation('warehouseMapModule');
  const { id } = useParams<{ id: string }>();
  const warehouseId = id ? parseInt(id, 10) : 0;

  const store = useWarehouseMapStore();
  const {
    editMode,
    currentSubMode,
    setEditMode,
    setCurrentSubMode,
    setDisplayRows,
    setDisplayCols,
    localRows,
    localCols,
    selectedCells,
    deletedRackIds,
    setDeletedRackIds,
    localWallCells,
    localRacks,
    setLocalRacks,
    preFillRow,
    setPreFillRow,
    preFillCol,
    setPreFillCol,
    showCreateForm,
    setShowCreateForm,
    showLocationModal,
    setShowLocationModal,
    selectedRack,
    setSelectedRack,
    pendingShelves,
    setPendingShelves,
    pendingSlotsPerShelf,
    setPendingSlotsPerShelf,
    pendingIsEndcap,
    setPendingIsEndcap,
    resetAll: storeResetAll,
  } = store;

  const gridQuery = useWarehouseGrid(warehouseId);
  const racksQuery = useWarehouseRacks(warehouseId);
  const warehouseQuery = useWarehouse(warehouseId);
  const heatmapQuery = useWarehouseHeatmap(warehouseId);
  const updateRackMutation = useUpdateRack(warehouseId);
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const heatmapShelves = useRackHeatmap(heatmapQuery.data, selectedRack?.id);

  // Sync display values when grid loads or is reset
  const baseGrid = gridQuery.data;
  useEffect(() => {
    if (baseGrid) {
      setDisplayRows(String(baseGrid.grid_rows));
      setDisplayCols(String(baseGrid.grid_cols));
    }
  }, [baseGrid, setDisplayCols, setDisplayRows]);

  // Regenerate grid with local wall cells applied (aisle ↔ wall toggle)
  const effectiveGridWithWalls = (() => {
    if (!baseGrid) return baseGrid;

    const effGrid = (() => {
      if (localRows === null && localCols === null) return baseGrid;
      const newRows = localRows ?? baseGrid.grid_rows;
      const newCols = localCols ?? baseGrid.grid_cols;

      const existingCells = new Map<string, GridCell>();
      for (const cell of baseGrid.cells) {
        existingCells.set(`${cell.y}:${cell.x}`, cell);
      }

      const regenerated: GridCell[] = [];
      for (let gy = 0; gy < newRows; gy++) {
        for (let gx = 0; gx < newCols; gx++) {
          const existing = existingCells.get(`${gy}:${gx}`);
          if (existing) {
            regenerated.push(existing);
          } else {
            regenerated.push({ x: gx, y: gy, type: 'aisle', walkable: true, rack_id: null });
          }
        }
      }

      for (let i = 0; i < newCols; i++) {
        const wallKey = `${newRows - 1}:${i}`;
        if (!existingCells.has(wallKey)) {
          const idx = regenerated.findIndex((c) => c.y === newRows - 1 && c.x === i);
          if (idx !== -1) regenerated[idx] = { ...regenerated[idx], type: 'wall', walkable: false };
        }
      }
      for (let gy = 0; gy < newRows; gy++) {
        const wallKey = `${gy}:${newCols - 1}`;
        if (!existingCells.has(wallKey)) {
          const idx = regenerated.findIndex((c) => c.y === gy && c.x === newCols - 1);
          if (idx !== -1) regenerated[idx] = { ...regenerated[idx], type: 'wall', walkable: false };
        }
      }

      return { ...baseGrid, grid_rows: newRows, grid_cols: newCols, cells: regenerated };
    })();

    const finalCells = effGrid.cells.map((cell) => {
      const key = `${cell.y}:${cell.x}`;
      if (localWallCells.has(key)) {
        return cell.type === 'wall'
          ? { ...cell, type: 'aisle', walkable: true }
          : { ...cell, type: 'wall', walkable: false };
      }
      return cell;
    });

    return { ...effGrid, cells: finalCells };
  })();

  // Sync layout mutation (grid + racks reconciliation)
  const syncLayoutMutation = useMutation({
    mutationFn: async () => {
      const httpClient = createHttpClient(apiClientWH);
      const adapter = new WarehouseMapApiAdapter(httpClient);

      const finalRacks = apiRacks.filter((r) => !deletedRackIds.has(r.id)).concat(localRacks);
      const racksPayload = finalRacks.map((r) => ({
        row: r.row,
        col: r.col,
        is_endcap: r.is_endcap,
        shelves: r.shelves,
        slots_per_shelf: r.slots_per_shelf,
      }));

      const allWallCells = new Set<string>();
      if (effectiveGridWithWalls) {
        for (const cell of effectiveGridWithWalls.cells) {
          if (cell.type === 'wall') {
            allWallCells.add(`${cell.y}:${cell.x}`);
          }
        }
      }

      const wallsPayload = Array.from(allWallCells).map((key) => {
        const [y, x] = key.split(':').map(Number);
        return { x, y };
      });

      return adapter.syncLayout(warehouseId, {
        grid_rows: localRows ?? baseGrid?.grid_rows,
        grid_cols: localCols ?? baseGrid?.grid_cols,
        walls: wallsPayload.length > 0 ? wallsPayload : undefined,
        racks: racksPayload.length > 0 ? racksPayload : undefined,
      });
    },
    onSuccess: async (data) => {
      storeResetAll();
      await queryClient.invalidateQueries({ queryKey: ['warehouse-grid', warehouseId] });
      await queryClient.invalidateQueries({ queryKey: ['warehouse-racks', warehouseId] });
      await queryClient.invalidateQueries({ queryKey: ['warehouse-heatmap', warehouseId] });

      const hasWarnings = data.warnings?.kept_racks?.length;
      if (hasWarnings) {
        const rackIds = data?.warnings?.kept_racks.join(', ');
        toast({
          title: t('toast.syncSuccess'),
          description: `${t('toast.rackIds', { ids: rackIds })} — ${data?.warnings?.message}`,
          variant: 'default',
        });
      } else {
        toast({ title: t('toast.syncSuccess'), variant: 'success' });
      }
    },
  });

  if (gridQuery.isLoading || racksQuery.isLoading) {
    return <div className="flex items-center justify-center h-64">{t('view.loading')}</div>;
  }
  if (gridQuery.error || racksQuery.error) {
    return <div className="text-red-600 p-4">{t('view.error')}</div>;
  }

  const grid = effectiveGridWithWalls ?? gridQuery.data;
  const apiRacks = racksQuery.data ?? [];
  const newCount = localRacks.length;
  const deleteCount = deletedRackIds.size;
  const wallCount = localWallCells.size;
  const hasLocalChanges = newCount > 0 || deleteCount > 0 || localRows !== null || localCols !== null || wallCount > 0;
  const mergedRacks = apiRacks.filter((r) => !deletedRackIds.has(r.id)).concat(localRacks);

  const handleCreate = () => {
    const row = preFillRow ?? 0;
    const col = preFillCol ?? 0;
    const newRack: Rack = {
      id: -1,
      warehouse_id: warehouseId,
      code: '',
      row,
      col,
      created_at: new Date().toISOString(),
      width: RACK_WIDTH,
      height: RACK_HEIGHT,
      depth: RACK_DEPTH,
      shelves: pendingShelves,
      slots_per_shelf: pendingSlotsPerShelf,
      is_endcap: pendingIsEndcap,
      updated_at: new Date().toISOString(),
      locations: [],
    };
    setLocalRacks((prev) => [...prev, newRack]);
    toast({ title: t('toast.rackAdded', { count: localRacks.length + 1 }) });
  };

  const handleDeleteMark = (rackIds?: number[]) => {
    if (!rackIds || rackIds.length === 0) {
      toast({ title: t('toast.noRackFound'), variant: 'default' });
      return;
    }

    setDeletedRackIds((prev) => {
      const hasAll = rackIds.every((id) => prev.has(id));
      if (hasAll) {
        const next = new Set([...prev].filter((id) => !rackIds.includes(id)));
        toast({ title: t('toast.rackUnmarked', { count: rackIds.length }), variant: 'default' });
        return next;
      }
      const next = new Set([...prev, ...rackIds]);
      toast({ title: t('toast.rackDeleted', { count: next.size }), variant: 'default' });
      return next;
    });
  };

  return (
    <>
      <WarehouseBreadcrumb warehouseName={warehouseQuery.data?.name} />
      <div className="px-6 pt-4 h-screen flex flex-col">
        {/* Mode selector */}
        <div className="mb-3">
          <div className="space-y-2">
            {/* Main mode header */}
            <div className="flex items-center justify-between">
              <div className="inline-flex rounded-sm border border-card-border p-[2px]">
                <button
                  onClick={() => {
                    setEditMode(false);
                    setCurrentSubMode('add');
                    setShowCreateForm(false);
                  }}
                  className={`px-3 py-1.5 text-xs font-medium rounded-sm transition-colors ${!editMode ? 'bg-background shadow-sm text-primary' : 'text-muted-foreground hover:text-foreground'}`}
                >
                  {t('view.viewMode')}
                </button>
                <button
                  onClick={() => {
                    setEditMode(true);
                    setCurrentSubMode('add');
                    setShowCreateForm(false);
                  }}
                  className={`px-3 py-1.5 text-xs font-medium rounded-sm transition-colors ${editMode ? 'bg-background shadow-sm text-primary' : 'text-muted-foreground hover:text-foreground'}`}
                >
                  {t('view.editMode')}
                </button>
              </div>
            </div>

            {/* Sub-mode */}
            {editMode && (
              <div className="inline-flex rounded-sm border border-card-border p-[2px]">
                {[
                  { mode: 'add' as const, label: t('view.addRack') },
                  { mode: 'delete' as const, label: t('view.deleteRack') },
                  { mode: 'wall' as const, label: t('view.wallMode') },
                ].map(({ mode, label }) => (
                  <button
                    key={mode}
                    onClick={() => setCurrentSubMode(mode)}
                    className={`px-3 py-1.5 text-xs font-medium rounded-sm transition-colors ${currentSubMode === mode ? 'bg-background shadow-sm text-primary' : 'text-muted-foreground hover:text-foreground'}`}
                  >
                    {label}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Change summary */}
        {editMode && hasLocalChanges && (
          <div className="flex gap-3 mb-4 items-center">
            <span className="text-xs font-medium text-muted-foreground">
              {t('view.changeSummaryNew', { count: newCount })}
            </span>
            <span className="text-xs font-medium text-muted-foreground">
              {t('view.changeSummaryDelete', { count: deleteCount })}
            </span>
            {wallCount > 0 && (
              <span className="text-xs font-medium text-muted-foreground">
                {t('view.changeSummaryWalls', { count: wallCount })}
              </span>
            )}
          </div>
        )}

        {/* Admin toolbar */}
        {editMode && (
          <WarehouseAdminToolbar
            baseGrid={baseGrid}
            syncLayoutMutation={syncLayoutMutation}
            currentSubMode={currentSubMode}
          />
        )}

        {/* Create / Delete Modal — only used in add mode */}
        {(currentSubMode === 'add' || showCreateForm) && (
          <RackFormModal
            open={showCreateForm}
            onOpenChange={(open) => !open && setShowCreateForm(false)}
            deleteMode={currentSubMode === 'delete'}
            preFillRow={preFillRow}
            preFillCol={preFillCol}
            pendingShelves={pendingShelves}
            pendingSlotsPerShelf={pendingSlotsPerShelf}
            pendingIsEndcap={pendingIsEndcap}
            localRacks={localRacks}
            deletedRackIds={deletedRackIds}
            onPreFillRowChange={(row) => setPreFillRow(row)}
            onPreFillColChange={(col) => setPreFillCol(col)}
            onShelvesChange={(shelves) => setPendingShelves(shelves)}
            onSlotsPerShelfChange={(slots) => setPendingSlotsPerShelf(slots)}
            onIsEndcapChange={(checked) => setPendingIsEndcap(checked)}
            onCreateAndClose={() => {
              handleCreate();
              setShowCreateForm(false);
            }}
            onDeleteAndClose={() => {
              const targetRow = preFillRow ?? 0;
              const targetCol = preFillCol ?? 0;
              const racksToDelete = apiRacks.filter((r) => r.row === targetRow && r.col === targetCol);
              if (racksToDelete.length > 0) {
                setDeletedRackIds((prev) => new Set([...prev, ...racksToDelete.map((r) => r.id)]));
              }
            }}
          />
        )}

        {/* Grid */}
        <div className="mt-4 flex-1">
          <GridCanvas
            grid={(effectiveGridWithWalls ?? grid) as GridData | undefined}
            racks={mergedRacks}
            localRackIds={localRacks.map((r) => r.id)}
            localWallCells={localWallCells}
            selectedCells={selectedCells}
            deletedRackIds={deletedRackIds}
            theme="dark"
            interactive={true}
            onCellClick={(cell) => {
              // Wall mode — toggle wall cell (only aisle cells, not docks)
              if (currentSubMode === 'wall' && cell.type !== 'dock') {
                store.toggleWallCell(cell.x, cell.y);
                return;
              }

              // Delete mode — mark rack for deletion without opening modal
              const rackAtCell = mergedRacks.find((r) => r.row === cell.y && r.col === cell.x);
              if (currentSubMode === 'delete' && (cell.type === 'rack' || rackAtCell)) {
                handleDeleteMark([rackAtCell?.id ?? cell.rack_id]);
                return;
              }

              // Add mode — open create modal on aisle cells (only in edit mode)
              if (editMode && currentSubMode === 'add' && cell.type !== 'rack') {
                const existingLocalRack = mergedRacks.find((r) => r.row === cell.y && r.col === cell.x && r.id < 0);
                setPreFillRow(cell.y);
                setPreFillCol(cell.x);

                if (existingLocalRack) {
                  // Clicking an existing local rack removes it
                  setLocalRacks((prev) => prev.filter((r) => r.id !== existingLocalRack.id));
                  toast({ title: t('toast.rackRemoved', { count: 1 }), variant: 'default' });
                } else {
                  // Empty cell — show the create modal
                  setShowCreateForm(true);
                }
                return;
              }

              // View mode or add mode on rack — show locations modal
              const viewModeRack = mergedRacks.find((r) => r.row === cell.y && r.col === cell.x);
              if ((cell.type === 'rack' && cell.rack_id !== null) || viewModeRack) {
                const rack = viewModeRack ?? mergedRacks.find((r) => r.id === cell.rack_id);
                if (rack) {
                  setSelectedRack(rack);
                  setShowLocationModal(true);
                }
              }
            }}
          />
        </div>

        {/* Rack Locations Modal */}
        <RackLocationModal
          rack={selectedRack}
          open={showLocationModal}
          onOpenChange={(open) => {
            if (!open) {
              setSelectedRack(null);
              setShowLocationModal(false);
            }
          }}
          editMode={editMode}
          onUpdateRack={updateRackMutation.mutateAsync}
          heatmapShelves={heatmapShelves}
        />
      </div>
    </>
  );
};
